import React, { useEffect } from "react";
import { getImageUrl, transformations } from "../../util/get-url-utils";
import PropTypes from "prop-types";
import {
  Type,
  Card,
  CardContent,
  CardMedia,
  Dialog,
  DialogTitle,
  DialogTitleActions,
  DialogImage,
} from "@ebscois/react-component-library";

import { StyledHeading, ItalicType } from "./_.styled";
import { useBaseMediaUrls } from "../../hooks/useBaseMediaUrls";
import ViewInContextString from "../ViewInContextString";
import { logImageViewed } from "../../util/analytics";

const DialogLightBoxImage = ({
  type = "image",
  images,
  currentImage,
  onNextImage,
  onPrevImage,
  isFullSizeOpen,
  onFullSizeModalToggle,
  isOpen,
  onRequestClose,
  affordance,
}) => {
  const cloudinaryUrl = useBaseMediaUrls("images");

  useEffect(() => {
    const image = images[currentImage];
    if ((isOpen && image) || isFullSizeOpen) {
      const params = {
        title: image.title,
        mediaId: image.publicId,
        type: image.format,
        affordance,
      };
      if (isFullSizeOpen) {
        params.affordance = "topic-modal";
      }
      logImageViewed(params);
    }
  }, [isOpen, isFullSizeOpen, currentImage, type]); // eslint-disable-line react-hooks/exhaustive-deps

  return images?.length ? (
    <>
      <Dialog
        isOpen={isOpen}
        maxWidth="lg"
        onRequestClose={onRequestClose}
        data-testid="modal-light-box-image-root"
      >
        <DialogTitle aria-live="polite">
          <Type
            element="h1"
            styledAs="p"
            fontSize="lg"
            textTransform="uppercase"
            data-testid="modal-light-box-image-counter"
          >
            Image {currentImage + 1} of {images.length}
          </Type>
          <DialogTitleActions
            disabledNext={images.length === 1}
            disabledPrevious={currentImage === 0}
            onNextClick={onNextImage}
            onPreviousClick={onPrevImage}
            onRequestClose={onRequestClose}
          />
        </DialogTitle>
        <Card borderColor="gray400" borderStyle="solid" borderWidth="1px">
          <CardMedia
            iconBackground="gray100"
            iconTransparency={0}
            borderYColor="gray400"
            borderYStyle="solid"
            borderYWidth="1px"
            data-element="media-image-card"
            onFullscreenClick={onFullSizeModalToggle}
            showFullscreenButton={true}
            data-testid="modal-light-box-image-media"
          >
            <img
              alt={images[currentImage]?.title}
              src={getImageUrl(
                images[currentImage]?.publicId,
                cloudinaryUrl,
                transformations.lightbox2x
              )}
              data-testid="modal-light-box-image"
              style={{ maxWidth: "inherit", maxHeight: "inherit" }}
            />
          </CardMedia>
          <CardContent>
            <StyledHeading
              element="h4"
              data-testid="modal-light-box-image-caption"
              fontWeight="semibold"
            >
              {images[currentImage]?.title}
            </StyledHeading>
            {images[currentImage]?.description && (
              <Type
                fontSize="sm"
                element="p"
                data-testid="modal-light-box-image-description"
              >
                {images[currentImage]?.description}
              </Type>
            )}
            {images[currentImage]?.associatedTopics &&
              ViewInContextString({
                associatedTopics: images[currentImage].associatedTopics,
                onRequestClose,
              })}
            <ItalicType
              fontSize="xs"
              element="p"
              paddingTop="sm"
              data-testid="modal-light-box-image-copyright"
            >
              {images[currentImage]?.copyright}
            </ItalicType>
          </CardContent>
        </Card>
      </Dialog>
      <Dialog
        aria-label="fullscreen dialog example"
        fullscreenDialog={true}
        isOpen={isFullSizeOpen}
        onRequestClose={onFullSizeModalToggle}
        showCloseButton
        data-testid="full-screen-image-dialog"
      >
        <DialogImage
          imageSrc={getImageUrl(
            images[currentImage]?.publicId,
            cloudinaryUrl,
            transformations.fullRes
          )}
          imageAlt={images[currentImage]?.title}
        />
      </Dialog>
    </>
  ) : (
    <></>
  );
};

DialogLightBoxImage.propTypes = {
  type: PropTypes.string,
  images: PropTypes.arrayOf(
    PropTypes.shape({
      caption: PropTypes.string.isRequired,
      copyright: PropTypes.string.isRequired,
      mediaHost: PropTypes.string.isRequired,
      publicId: PropTypes.string.isRequired,
      topicTitle: PropTypes.string.isRequired,
      topicUrl: PropTypes.string.isRequired,
    })
  ),
  currentImage: PropTypes.number.isRequired,
  onNextImage: PropTypes.func.isRequired,
  onPrevImage: PropTypes.func.isRequired,
  onFullSizeModalToggle: PropTypes.func.isRequired,
};

DialogLightBoxImage.displayName = "DialogLightBoxImage";

export default DialogLightBoxImage;
